"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import { useAuth } from "../../../../context/AuthContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import Button from "../../../../components/Button";
import Checkbox from "../../../../components/Checkbox";
import SmallerSpinner from "../../../../components/SmallerSpinner.jsx";
import MedicationCard from "../../../Dispensary/Script/components/views/MedicationCard.jsx";
import GenericPopup from "../../../Dispensary/Script/views/GenericPopup.jsx";
import useGenericSubstitution from "../../../Dispensary/Script/hooks/useGenericSubstitution.js";
import ErrorModal from "../../../../components/ErrorModal";
 
import Pagination from "../../../../components/Pagination";
import styles from "../css/prescription.module.css";

export default function Prescription() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const { user } = useAuth();
  const isPCDT = Number(user?.roleid) === 8;

  // Check if user is in PCDT flow (came from treatment service)
  const serviceWorkflowData = getStepDraft('serviceWorkflowData', {});
  const hasServiceWorkflowData = Object.keys(serviceWorkflowData).length > 0;
  const returnedFromWorkflow = consultationData?.returnedFromWorkflow || false;
  const isInPCDTFlow = isPCDT && hasServiceWorkflowData && returnedFromWorkflow;

  const draftKey = "prescription";

  const defaultDraft = {
    medications: [],
    repeatScript: false,
    chronicMedication: false,
    repeatCount: 2,
    currentPage: 1,
  };

  const initialDraft = getStepDraft(draftKey, defaultDraft);
  
  const [medications, setMedications] = useState(initialDraft.medications || []);
  const [repeatScript, setRepeatScript] = useState(!!initialDraft.repeatScript);
  const [chronicMedication, setChronicMedication] = useState(!!initialDraft.chronicMedication);
  const [repeatCount, setRepeatCount] = useState(initialDraft.repeatCount ?? 2);
  const [currentPage, setCurrentPage] = useState(initialDraft.currentPage ?? 1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [validationMessage, setValidationMessage] = useState("");

  const generic = useGenericSubstitution({ medications, setMedications });

  const medicationsPerPage = 3;
  const totalPages = Math.ceil(medications.length / medicationsPerPage);
  const startIndex = (currentPage - 1) * medicationsPerPage;
  const endIndex = startIndex + medicationsPerPage;
  const currentMedications = medications.slice(startIndex, endIndex);

  const handleAddMedication = () => {
    const newId = medications.length ? Math.max(...medications.map((m) => m.id)) + 1 : 1;
    const newMedications = [
      ...medications,
      {
        id: newId,
        drugName: "",
        quantity: 1,
        packSize: "",
        strength: "",
        icd10: "",
        icd10Options: [],
        instructions: "",
        instructionsOther: "",
        sku: null,
        nappiCode: "",
        costPrice: "",
        stockText: "",
        stockQty: undefined,
        toFollowStruck: false,
        deliveryOption: "",
        saved: false,
      },
    ];
    setMedications(newMedications);
    setStepDraft(draftKey, (prev) => ({
      ...(prev || defaultDraft),
      medications: newMedications,
    }));
    const newTotalPages = Math.ceil(newMedications.length / medicationsPerPage);
    setCurrentPage(newTotalPages);
    setStepDraft(draftKey, (prev) => ({
      ...(prev || defaultDraft),
      currentPage: newTotalPages,
    }));
  };

  const handleDeleteMedication = (id) => {
    const newMedications = medications.filter((med) => med.id !== id);
    setMedications(newMedications);
    setStepDraft(draftKey, (prev) => ({
      ...(prev || defaultDraft),
      medications: newMedications,
    }));
    const newTotalPages = Math.ceil(newMedications.length / medicationsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
      setStepDraft(draftKey, (prev) => ({
        ...(prev || defaultDraft),
        currentPage: newTotalPages,
      }));
    }
  };

  const handleMedicationChange = (id, field, value) => {
    setMedications((prev) => {
      const next = prev.map((med) => (med.id === id ? { ...med, [field]: value } : med));
      setStepDraft(draftKey, (d) => ({
        ...(d || defaultDraft),
        medications: next,
      }));
      return next;
    });
  };

  const validateLastMedication = () => {
    if (medications.length === 0) return true;
    
    const lastMed = medications[medications.length - 1];
    const hasDrugName = !!(lastMed.drugName && String(lastMed.drugName).trim() !== "");
    const hasDirections = !!(lastMed.instructions && (lastMed.instructions !== "Other" || (lastMed.instructionsOther && String(lastMed.instructionsOther).trim() !== "")));
    
    return hasDrugName && hasDirections;
  };

  const validateAllMedications = () => {
    console.log("validateAllMedications called with medications:", medications);
    
    // Allow proceeding even if no medications are added
    if (medications.length === 0) {
      console.log("No medications found - allowing to proceed");
      return true;
    }

    // If there are medications, validate they are complete
    for (const med of medications) {
      const hasDrugName = !!(med.drugName && String(med.drugName).trim() !== "");
      const hasDirections = !!(med.instructions && (med.instructions !== "Other" || (med.instructionsOther && String(med.instructionsOther).trim() !== "")));
      
      console.log("Checking medication:", med);
      console.log("hasDrugName:", hasDrugName);
      console.log("hasDirections:", hasDirections);
      
      if (!hasDrugName) {
        console.log("Missing drug name");
        setValidationMessage("Please enter a drug name for all medications or remove incomplete medications.");
        return false;
      }
      
      if (!hasDirections) {
        console.log("Missing directions");
        setValidationMessage("Please select directions for all medications or remove incomplete medications.");
        return false;
      }
    }

    console.log("All validations passed");
    return true;
  };

  const handleSaveMedication = (id) => {
    const med = medications.find((m) => m.id === id);
    if (!med) return;
    if (med.saved) {
      return;
    }
    const hasDirections = !!(med.instructions && (med.instructions !== "Other" || (med.instructionsOther && String(med.instructionsOther).trim() !== "")));
    if (!hasDirections) {
      setValidationMessage("Please select directions for this medication.");
      return;
    }
    // Mark medication as saved without showing generic popup
    setMedications((prev) => {
      const next = prev.map((m) => (m.id === id ? { ...m, saved: true } : m));
      setStepDraft(draftKey, (d) => ({
        ...(d || defaultDraft),
        medications: next,
      }));
      return next;
    });
  };

  const handleGenericClose = () => {
    const id = generic.savingMedId;
    if (id != null) {
      setMedications((prev) => {
        const next = prev.map((m) => (m.id === id ? { ...m, saved: true } : m));
        setStepDraft(draftKey, (d) => ({
          ...(d || defaultDraft),
          medications: next,
        }));
        return next;
      });
    }
    generic.closePopup();
  };

  const handleGenericReplace = async (row) => {
    const id = generic.savingMedId;
    await generic.replaceWithSku(row);
    if (id != null) {
      setMedications((prev) => {
        const next = prev.map((m) => (m.id === id ? { ...m, saved: true } : m));
        setStepDraft(draftKey, (d) => ({
          ...(d || defaultDraft),
          medications: next,
        }));
        return next;
      });
    }
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/treatment-plan");
  };

  const handleNext = async () => {
    setSaveError("");
    setValidationMessage("");

    console.log("handleNext called");
    console.log("medications:", medications);
    console.log("isPCDT:", isPCDT);
    console.log("isInPCDTFlow:", isInPCDTFlow);

    if (!validateAllMedications()) {
      console.log("validateAllMedications failed");
      return;
    }

    console.log("validateAllMedications passed");

    try {
      setSaving(true);

      setStepDraft(draftKey, {
        medications,
        repeatScript,
        chronicMedication,
        repeatCount,
        currentPage,
      });

      console.log("Calling nextStep()");
      nextStep();
      
      // For PCDT users in treatment flow, go to invoice after prescription
      // For normal 10-step consultation (including PCDT users), go to review-follow-up
      if (isInPCDTFlow) {
        console.log("Navigating to invoice (PCDT flow)");
        navigate("/customer/clinic/invoice");
      } else {
        console.log("Navigating to review-follow-up (normal consultation)");
        navigate("/customer/clinic/review-follow-up");
      }
    } catch (e) {
      console.error("Error in handleNext:", e);
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={isInPCDTFlow ? 1 : 9}
            totalSteps={isInPCDTFlow ? 1 : 10}
            stepTitle="Prescription"
            stepDetails={isInPCDTFlow ? "Step 1 of 1 · Maria van der Merwe · General Consultation" : "Step 9 of 10 · Maria van der Merwe · General Consultation"}
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            <div className={styles.medicationSection}>
              <div className={styles.sectionHeader}>
                <Button variant="primary" onClick={handleAddMedication} className={styles.addButton} disabled={!validateLastMedication()}>
                  + Add
                </Button>
              </div>

              <div className={styles.medicationGrid}>
                {currentMedications.map((medication, pageIndex) => {
                  const overallIndex = medications.findIndex(m => m.id === medication.id);
                  return (
                    <MedicationCard
                      key={medication.id}
                      med={medication}
                      onChange={handleMedicationChange}
                      onDelete={handleDeleteMedication}
                      index={overallIndex}
                      onSave={handleSaveMedication}
                      showSaveButton={false}
                    />
                  );
                })}
              </div>
            </div>

            <div className={styles.repeatSection}>
              <div className={styles.repeatHeader}>
                <Checkbox
                  id="repeatScript"
                  label="Repeat Script"
                  checked={repeatScript}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRepeatScript(checked);
                    setStepDraft(draftKey, (prev) => ({
                      ...(prev || defaultDraft),
                      repeatScript: checked,
                      // If user unticks repeat, keep repeatCount but script will send 0.
                      repeatCount: prev?.repeatCount ?? repeatCount,
                    }));
                  }}
                />
                {isPCDT && (
                  <Checkbox
                    id="chronicMedication"
                    label="Chronic Medication"
                    checked={chronicMedication}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setChronicMedication(checked);
                      setStepDraft(draftKey, (prev) => ({
                        ...(prev || defaultDraft),
                        chronicMedication: checked,
                      }));
                    }}
                  />
                )}
              </div>

              {repeatScript && (
                <div className={styles.repeatControls}>
                  <SmallerSpinner
                    value={Number.isFinite(repeatCount) ? repeatCount : 1}
                    min={1}
                    step={1}
                    ariaLabel="Repeat script count"
                    compact
                    onChange={(v) => {
                      setRepeatCount(v);
                      setStepDraft(draftKey, (prev) => ({
                        ...(prev || defaultDraft),
                        repeatCount: v,
                      }));
                    }}
                  />
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            )}

            <div className={styles.completeContainer}>
              {saveError && (
                <div className={styles.inlineError}>{saveError}</div>
              )}
              {/* <Button variant="primary" onClick={handleNext} className={styles.completeButton} disabled={saving}>
                {saving ? 'Completing...' : 'Next'}
              </Button> */}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
